import React, { useState, useEffect } from "react";
import axios from "axios";
import * as Icon from "react-feather";
import { useHistory } from "react-router-dom";
const API_URL = process.env.REACT_APP_BASEURL + "/api/aiStudio";

const AIStudioManager = () => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [formData, setFormData] = useState({
    path: "",
    name: "",
    type: "",
    url: "",
    parameters: [{ key: "", value: "" }],
    plan: [],
    image: null,
    enabled: true,
    // Campos opcionales para App Launcher
    icon: "",
    color: "#e91e63",
    description: "",
    category: "ai",
    order: "",
  });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [configToEdit, setConfigToEdit] = useState(null);
  const planOptions = [
    { id: 1, label: "Bronze" },
    { id: 2, label: "Silver" },
    { id: 3, label: "Gold" },
    { id: 4, label: "Platinum" },
  ];

  const history = useHistory();

  React.useEffect(() => {
    const tkn = window.sessionStorage.getItem("Token");
    const usr = window.sessionStorage.getItem("User");
    if (!tkn || !usr) {
      history.push("/");
    }
  }, []);

  // Fetch configs on mount
  useEffect(() => {
    setLoading(true);
    axios
      .get(API_URL)
      .then((res) => {
        setConfigs(res.data.data || res.data);
        setLoading(false);
      })
      .catch(() => {
        setMsg({ type: "danger", text: "Failed to load configurations." });
        setLoading(false);
      });
  }, []);

  const handleOpen = async () => {
    if (editing) {
      // Save the current edit before opening add
      await handleUpdate();
    }
    setAdding(true);
    setEditing(false);
    setConfigToEdit(null);
    setFormData({
      path: "",
      name: "",
      type: "",
      url: "",
      parameters: [{ key: "", value: "" }],
      plan: [],
      image: null,
      enabled: true,
      icon: "",
      color: "#e91e63",
      description: "",
      category: "ai",
      order: "",
    });
  };

  const handleClose = () => {
    setAdding(false);
    setEditing(false);
    setConfigToEdit(null);
    setFormData({
      path: "",
      name: "",
      type: "",
      url: "",
      parameters: [{ key: "", value: "" }],
      plan: [],
      image: null,
      enabled: true,
      icon: "",
      color: "#e91e63",
      description: "",
      category: "ai",
      order: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleParamChange = (idx, field, value) => {
    const newParams = [...formData.parameters];
    newParams[idx] = { ...newParams[idx], [field]: value };
    setFormData({ ...formData, parameters: newParams });
  };

  const handleAddParam = () => {
    setFormData({
      ...formData,
      parameters: [...formData.parameters, { key: "", value: "" }],
    });
  };

  const handleRemoveParam = (idx) => {
    const newParams = formData.parameters.filter((_, i) => i !== idx);
    setFormData({
      ...formData,
      parameters: newParams.length ? newParams : [{ key: "", value: "" }],
    });
  };

  const handlePlanChange = (planId) => {
    setFormData((prev) => {
      const exists = prev.plan.includes(planId);
      return {
        ...prev,
        plan: exists
          ? prev.plan.filter((id) => id !== planId)
          : [...prev.plan, planId],
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async () => {
    if (
      !formData.path ||
      !formData.name ||
      !formData.type ||
      !formData.url ||
      !formData.plan.length
    ) {
      setMsg({
        type: "danger",
        text: "Please fill in all required fields (Path, Name, Type, URL) and select at least one plan.",
      });
      return;
    }

    setCreating(true);

    try {
      const additionalParams = formData.parameters.reduce((acc, param) => {
        if (param.key && param.value) {
          acc[param.key] = param.value;
        }
        return acc;
      }, {});

      const formDataToSend = new FormData();
      formDataToSend.append("path", formData.path);
      formDataToSend.append("name", formData.name);
      formDataToSend.append("type", formData.type);
      formDataToSend.append("url", formData.url);
      formDataToSend.append(
        "additionalParams",
        JSON.stringify(additionalParams)
      );
      formDataToSend.append("plan", JSON.stringify(formData.plan));
      formDataToSend.append("enabled", formData.enabled);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }
      // Campos opcionales para App Launcher
      if (formData.icon) {
        formDataToSend.append("icon", formData.icon);
      }
      if (formData.color) {
        formDataToSend.append("color", formData.color);
      }
      if (formData.description) {
        formDataToSend.append("description", formData.description);
      }
      if (formData.category) {
        formDataToSend.append("category", formData.category);
      }
      if (formData.order) {
        formDataToSend.append("order", formData.order);
      }

      const res = await axios.post(API_URL, formDataToSend);

      setConfigs([...configs, res.data.data || res.data]);
      setMsg({ type: "success", text: "Configuration added successfully." });
      handleClose();
      setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      console.error("Error submitting form:", err);
      setMsg({ type: "danger", text: "Failed to add configuration." });
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (config) => {
    if (adding) {
      handleClose(); // Close add form before editing
    }
    setConfigToEdit(config.id);
    setEditing(true);
    setAdding(false);
    setFormData({
      path: config.path,
      name: config.name,
      type: config.type,
      url: config.url,
      parameters: Object.entries(config.additionalParams || {}).map(
        ([key, value]) => ({
          key,
          value,
        })
      ) || [{ key: "", value: "" }],
      plan: Array.isArray(config.plan) ? config.plan : [],
      image: null,
      enabled: config.enabled !== undefined ? config.enabled : true,
      // Campos opcionales para App Launcher
      icon: config.icon || "",
      color: config.color || "#e91e63",
      description: config.description || "",
      category: config.category || "ai",
      order: config.order || "",
    });
  };

  const handleUpdate = async () => {
    if (
      !formData.path ||
      !formData.name ||
      !formData.type ||
      !formData.url ||
      !formData.plan.length
    ) {
      setMsg({
        type: "danger",
        text: "Please fill in all required fields (Path, Name, Type, URL) and select at least one plan.",
      });
      return;
    }
    setUpdating(true);
    try {
      // Get the existing config
      const existingConfig = configs.find((c) => c.id === configToEdit);
      if (!existingConfig) {
        throw new Error("Configuration not found");
      }

      // Convert parameters array to object with key-value pairs
      const additionalParams = formData.parameters.reduce((acc, param) => {
        if (param.key && param.value) {
          acc[param.key] = param.value;
        }
        return acc;
      }, {});

      // Create the update data object
      const updateData = {
        path: formData.path || existingConfig.path,
        name: formData.name || existingConfig.name,
        type: existingConfig.type, // Always use existing type
        url: formData.url || existingConfig.url,
        additionalParams: additionalParams,
        plan: formData.plan.length ? formData.plan : existingConfig.plan,
        enabled: formData.enabled,
        // Campos opcionales para App Launcher
        icon: formData.icon !== undefined ? formData.icon : existingConfig.icon,
        color: formData.color || existingConfig.color || "#e91e63",
        description: formData.description !== undefined ? formData.description : existingConfig.description,
        category: formData.category || existingConfig.category || "ai",
        order: formData.order !== undefined ? (formData.order ? parseInt(formData.order) : null) : existingConfig.order,
      };

      // If there's a new image, use FormData
      if (formData.image instanceof File) {
        const formDataToSend = new FormData();
        Object.entries(updateData).forEach(([key, value]) => {
          formDataToSend.append(
            key,
            typeof value === "object" ? JSON.stringify(value) : value
          );
        });
        formDataToSend.append("image", formData.image);

        const res = await axios.put(
          `${API_URL}/${configToEdit}`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // If no new image, send as regular JSON
        const res = await axios.put(`${API_URL}/${configToEdit}`, updateData);
      }

      // Refetch all configs to ensure fresh data
      const resAll = await axios.get(API_URL);
      setConfigs(resAll.data.data || resAll.data);
      setMsg({ type: "success", text: "Configuration updated successfully." });
      handleClose();
      setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      console.error("Update error:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to update configuration.";
      setMsg({ type: "danger", text: errorMessage });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    setConfigToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await axios.delete(`${API_URL}/${configToDelete}`);
      setConfigs(configs.filter((c) => c.id !== configToDelete));
      setMsg({ type: "success", text: "Configuration deleted successfully." });
      setTimeout(() => setMsg(null), 2000);
      setDeleting(false);
    } catch (err) {
      setMsg({ type: "danger", text: "Failed to delete configuration." });
    } finally {
      setShowDeleteConfirm(false);
      setConfigToDelete(null);
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setConfigToDelete(null);
  };

  return (
    <>
      <div className="mx-4" style={{ marginTop: "0px" }}>
        <div className="d-flex justify-content-between align-items-center py-2">
          <h5 className="section-title">AI Studio Manager</h5>
        </div>
        {msg && (
          <div
            className={`alert alert-${msg.type} fixed-bottom fade show`}
            style={{ zIndex: 9999 }}
          >
            {msg.text}
            <button
              type="button"
              className="close"
              onClick={() => setMsg(null)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="container">
            <div className="row">
              {/* Add New Configuration Card/Form */}
              <div className="col-md-4 mb-4">
                <div className={`card h-100 plan-card`}>
                  <div className="card-body">
                    {adding ? (
                      <>
                        <div className="form-group mb-4">
                          <label className="form-label">
                            Path <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Tag size={16} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="path"
                              value={formData.path}
                              onChange={handleInputChange}
                              placeholder="Enter Path"
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">
                            Name <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Tag size={16} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Enter Name"
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">
                            Type <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.List size={16} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="type"
                              value={formData.type}
                              onChange={handleInputChange}
                              placeholder="Enter Type"
                              readOnly={editing}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">
                            URL <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Link size={16} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="url"
                              value={formData.url}
                              onChange={handleInputChange}
                              placeholder="Enter URL"
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="enabled"
                              name="enabled"
                              checked={formData.enabled}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="enabled"
                            >
                              Enable Extension
                            </label>
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">Parameters</label>
                          <div className="table-responsive">
                            {formData.parameters.map((param, idx) => (
                              <tr key={idx}>
                                <td>
                                  <input
                                    value={param.key}
                                    type="text"
                                    className="form-control"
                                    onChange={(e) =>
                                      handleParamChange(
                                        idx,
                                        "key",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Parameter Key"
                                  />
                                </td>
                                <td>
                                  <input
                                    value={param.value}
                                    type="text"
                                    className="form-control"
                                    onChange={(e) =>
                                      handleParamChange(
                                        idx,
                                        "value",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Parameter Value"
                                  />
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => handleRemoveParam(idx)}
                                    disabled={formData.parameters.length === 1}
                                    title="Remove"
                                  >
                                    <Icon.Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </div>
                          <button
                            className="btn btn-outline-primary w-100 add-service-btn mt-2"
                            type="button"
                            onClick={handleAddParam}
                          >
                            <Icon.Plus size={16} className="me-2" /> Add
                            Parameter
                          </button>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">
                            Plan <span className="text-danger">*</span>
                          </label>
                          <div className="d-flex gap-3">
                            {planOptions.map((option) => (
                              <div key={option.id} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`plan-${option.id}`}
                                  checked={formData.plan.includes(option.id)}
                                  onChange={() => handlePlanChange(option.id)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`plan-${option.id}`}
                                >
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">Image</label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Image size={16} />
                            </span>
                            <input
                              type="file"
                              className="form-control"
                              accept="image/svg+xml,image/png"
                              onChange={handleImageChange}
                            />
                          </div>
                          {formData.image && (
                            <div className="mt-2">
                              <img
                                src={
                                  formData.image instanceof File
                                    ? URL.createObjectURL(formData.image)
                                    : formData.image
                                }
                                alt="Preview"
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "200px",
                                }}
                              />
                            </div>
                          )}
                        </div>
                        {/* Campos opcionales para App Launcher */}
                        <div className="form-group mb-4">
                          <label className="form-label">Icon (Material-UI)</label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Image size={16} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="icon"
                              value={formData.icon}
                              onChange={handleInputChange}
                              placeholder="Ej: VideocamOutlined, SchoolOutlined"
                            />
                          </div>
                          <small className="text-muted">
                            Nombre del icono de Material-UI (opcional)
                          </small>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">Color</label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Droplet size={16} />
                            </span>
                            <input
                              type="color"
                              className="form-control form-control-color"
                              name="color"
                              value={formData.color}
                              onChange={handleInputChange}
                              style={{ height: "38px" }}
                            />
                            <input
                              type="text"
                              className="form-control"
                              name="color"
                              value={formData.color}
                              onChange={handleInputChange}
                              placeholder="#e91e63"
                            />
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">Description</label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.FileText size={16} />
                            </span>
                            <textarea
                              className="form-control"
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              placeholder="Descripción de la aplicación"
                              rows="2"
                            />
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">Category</label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Folder size={16} />
                            </span>
                            <select
                              className="form-control"
                              name="category"
                              value={formData.category}
                              onChange={handleInputChange}
                            >
                              <option value="ai">AI</option>
                              <option value="academic">Academic</option>
                              <option value="communication">Communication</option>
                              <option value="content">Content</option>
                              <option value="tools">Tools</option>
                              <option value="system">System</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-group mb-4">
                          <label className="form-label">Order</label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon.Hash size={16} />
                            </span>
                            <input
                              type="number"
                              className="form-control"
                              name="order"
                              value={formData.order}
                              onChange={handleInputChange}
                              placeholder="Orden de visualización"
                              min="0"
                            />
                          </div>
                          <small className="text-muted">
                            Orden de visualización en App Launcher (opcional)
                          </small>
                        </div>
                        <div className="d-flex gap-2 mt-4">
                          <button
                            className="btn btn-light flex-grow-1"
                            onClick={handleClose}
                          >
                            Cancel
                          </button>
                          {editing ? (
                            updating ? (
                              <button className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center">
                                <span className="spinner-border spinner-border-sm me-2" />
                                Update
                              </button>
                            ) : (
                              <button
                                className="btn btn-primary flex-grow-1"
                                onClick={handleUpdate}
                                disabled={updating}
                              >
                                Update
                              </button>
                            )
                          ) : creating ? (
                            <button className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center">
                              <span className="spinner-border spinner-border-sm me-2" />
                              Create
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary flex-grow-1"
                              onClick={handleSubmit}
                              disabled={creating}
                            >
                              Create
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div
                        className="add-plan-placeholder"
                        onClick={handleOpen}
                        style={{ cursor: "pointer" }}
                      >
                        <div
                          className="icon-wrapper"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: 80,
                          }}
                        >
                          <Icon.PlusCircle size={48} />
                        </div>
                        <h5>Add New Configuration</h5>
                        <p className="text-muted">
                          Click to create a new AI Studio configuration
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Existing Config Cards */}
              {configs.length > 0 &&
                configs.map((config, index) => (
                  <div className="col-md-4 mb-4" key={index}>
                    <div className="card h-100 plan-card">
                      <div
                        className="card-header"
                        style={{
                          background: "#4b4fa7",
                          color: "#fff",
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="card-title mb-0">{config.name}</h5>
                          <div>
                            <button
                              className="btn btn-sm me-2"
                              style={{ color: "white" }}
                              onClick={() => handleEdit(config)}
                            >
                              <Icon.Edit size={16} />
                            </button>
                            <button
                              className="btn btn-sm"
                              style={{ color: "white" }}
                              onClick={() => handleDelete(config.id)}
                            >
                              <Icon.Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        {editing && configToEdit === config.id ? (
                          <>
                            <div className="form-group mb-4">
                              <label className="form-label">
                                Path <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Tag size={16} />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="path"
                                  value={formData.path}
                                  onChange={handleInputChange}
                                  placeholder="Enter Path"
                                  required
                                />
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">
                                Name <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Tag size={16} />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="name"
                                  value={formData.name}
                                  onChange={handleInputChange}
                                  placeholder="Enter Name"
                                  required
                                />
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">
                                Type <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.List size={16} />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="type"
                                  value={formData.type}
                                  onChange={handleInputChange}
                                  placeholder="Enter Type"
                                  readOnly={editing}
                                  required
                                />
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">
                                URL <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Link size={16} />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="url"
                                  value={formData.url}
                                  onChange={handleInputChange}
                                  placeholder="Enter URL"
                                  required
                                />
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="enabled"
                                  name="enabled"
                                  checked={formData.enabled}
                                  onChange={handleInputChange}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="enabled"
                                >
                                  Enable Extension
                                </label>
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">Parameters</label>
                              <div className="table-responsive">
                                {formData.parameters.map((param, idx) => (
                                  <tr key={idx}>
                                    <td>
                                      <input
                                        value={param.key}
                                        type="text"
                                        className="form-control"
                                        onChange={(e) =>
                                          handleParamChange(
                                            idx,
                                            "key",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Parameter Key"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        value={param.value}
                                        type="text"
                                        className="form-control"
                                        onChange={(e) =>
                                          handleParamChange(
                                            idx,
                                            "value",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Parameter Value"
                                      />
                                    </td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={() => handleRemoveParam(idx)}
                                        disabled={
                                          formData.parameters.length === 1
                                        }
                                        title="Remove"
                                      >
                                        <Icon.Trash2 size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </div>
                              <button
                                className="btn btn-outline-primary w-100 add-service-btn mt-2"
                                type="button"
                                onClick={handleAddParam}
                              >
                                <Icon.Plus size={16} className="me-2" /> Add
                                Parameter
                              </button>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">
                                Plan <span className="text-danger">*</span>
                              </label>
                              <div className="d-flex gap-3">
                                {planOptions.map((option) => (
                                  <div key={option.id} className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`plan-${option.id}`}
                                      checked={formData.plan.includes(
                                        option.id
                                      )}
                                      onChange={() =>
                                        handlePlanChange(option.id)
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`plan-${option.id}`}
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">Image</label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Image size={16} />
                                </span>
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="image/svg+xml,image/png"
                                  onChange={handleImageChange}
                                />
                              </div>
                              {formData.image && (
                                <div className="mt-2">
                                  <img
                                    src={
                                      formData.image instanceof File
                                        ? URL.createObjectURL(formData.image)
                                        : formData.image
                                    }
                                    alt="Preview"
                                    style={{
                                      maxWidth: "200px",
                                      maxHeight: "200px",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            {/* Campos opcionales para App Launcher */}
                            <div className="form-group mb-4">
                              <label className="form-label">Icon (Material-UI)</label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Image size={16} />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="icon"
                                  value={formData.icon}
                                  onChange={handleInputChange}
                                  placeholder="Ej: VideocamOutlined, SchoolOutlined"
                                />
                              </div>
                              <small className="text-muted">
                                Nombre del icono de Material-UI (opcional)
                              </small>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">Color</label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Droplet size={16} />
                                </span>
                                <input
                                  type="color"
                                  className="form-control form-control-color"
                                  name="color"
                                  value={formData.color}
                                  onChange={handleInputChange}
                                  style={{ height: "38px" }}
                                />
                                <input
                                  type="text"
                                  className="form-control"
                                  name="color"
                                  value={formData.color}
                                  onChange={handleInputChange}
                                  placeholder="#e91e63"
                                />
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">Description</label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.FileText size={16} />
                                </span>
                                <textarea
                                  className="form-control"
                                  name="description"
                                  value={formData.description}
                                  onChange={handleInputChange}
                                  placeholder="Descripción de la aplicación"
                                  rows="2"
                                />
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">Category</label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Folder size={16} />
                                </span>
                                <select
                                  className="form-control"
                                  name="category"
                                  value={formData.category}
                                  onChange={handleInputChange}
                                >
                                  <option value="ai">AI</option>
                                  <option value="academic">Academic</option>
                                  <option value="communication">Communication</option>
                                  <option value="content">Content</option>
                                  <option value="tools">Tools</option>
                                  <option value="system">System</option>
                                </select>
                              </div>
                            </div>
                            <div className="form-group mb-4">
                              <label className="form-label">Order</label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <Icon.Hash size={16} />
                                </span>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="order"
                                  value={formData.order}
                                  onChange={handleInputChange}
                                  placeholder="Orden de visualización"
                                  min="0"
                                />
                              </div>
                              <small className="text-muted">
                                Orden de visualización en App Launcher (opcional)
                              </small>
                            </div>
                            <div className="d-flex gap-2 mt-4">
                              <button
                                className="btn btn-light flex-grow-1"
                                onClick={handleClose}
                              >
                                Cancel
                              </button>
                              {updating ? (
                                <button className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center">
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Update
                                </button>
                              ) : (
                                <button
                                  className="btn btn-primary flex-grow-1"
                                  onClick={handleUpdate}
                                  disabled={updating}
                                >
                                  Update
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="plan-detail mb-3">
                              <div className="detail-label">
                                <Icon.List size={16} className="me-2" />
                                Type
                              </div>
                              <div className="detail-value">{config.type}</div>
                            </div>
                            <div className="plan-detail mb-3">
                              <div className="detail-label">
                                <Icon.Tag size={16} className="me-2" />
                                Path
                              </div>
                              <div className="detail-value">{config.path}</div>
                            </div>
                            <div className="plan-detail mb-3">
                              <div className="detail-label">
                                <Icon.Link size={16} className="me-2" />
                                URL
                              </div>
                              <div
                                className="detail-value"
                                style={{ wordBreak: "break-all" }}
                              >
                                {config.url}
                              </div>
                            </div>
                            {config.additionalParams &&
                              Object.keys(config.additionalParams).length >
                                0 && (
                                <div
                                  className="plan-detail mb-3"
                                  style={{
                                    background: "#f6f7fb",
                                    borderRadius: 8,
                                    padding: "12px 16px",
                                  }}
                                >
                                  <div
                                    className="detail-label"
                                    style={{
                                      color: "#6c63b5",
                                      fontWeight: 600,
                                      marginBottom: 8,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Icon.Hash size={16} className="me-2" />{" "}
                                    Parameters
                                  </div>
                                  <div>
                                    {Object.entries(
                                      config.additionalParams
                                    ).map(([k, v]) => (
                                      <div
                                        key={k}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          marginBottom: 4,
                                          fontFamily: "monospace",
                                          fontSize: 15,
                                        }}
                                      >
                                        <span style={{ color: "#888" }}>
                                          {k}:
                                        </span>
                                        <span
                                          style={{
                                            color: "#222",
                                            marginLeft: 8,
                                          }}
                                        >
                                          {v}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            {config.image && (
                              <div className="plan-detail mb-3">
                                <div className="detail-label">
                                  <Icon.Image size={16} className="me-2" />
                                  Image
                                </div>
                                <div className="detail-value">
                                  <img
                                    src={config.image}
                                    alt={config.name}
                                    style={{
                                      maxWidth: "200px",
                                      maxHeight: "200px",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            <a
                              href={config.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary w-100 mt-2"
                            >
                              <Icon.ExternalLink size={16} className="me-2" />
                              Open Application
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
      {showDeleteConfirm && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1050,
          }}
        >
          <div
            className="modal-dialog"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
            }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelDelete}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete this configuration? This
                  action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                {deleting ? (
                  <button className="btn btn-danger d-flex align-items-center justify-content-center">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Delete
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIStudioManager;
